import useAUTH from 'providers/Auth/useAuth';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  getExploitationTypes,
  getGeofoliaPreflight,
  getSecteurLight,
  importParcelsGeofolia,
} from 'services/API/Cooperative';
import Table from 'components/generics/Table/Table';
import Button from 'components/generics/Button';
import Loader from 'components/generics/Loader';
import { toast } from 'react-toastify';
import { displayErrorMessage } from 'utils/tools_functions';
import { useCheckStatus } from 'providers/ImportStatus';
import ExploitationTableRowGeofolia from './table/ExploitationTableRowGeofolia';
import { IMPORT_FROM } from 'utils/GlobalVariables';

function GeofoliaProcess() {
  const [preflightJobId, setPreflightJobId] = useState(null);
  const [importJobId, setImportJobId] = useState(null);
  const [cooperativeId, setCooperativeId] = useState(null);
  const [exploitationTypes, setExploitationTypes] = useState(null);
  const [cooperativeSectors, setCooperativeSectors] = useState(null);
  const [exploitationsToImport, setExploitationsToImport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dataToHandle, setDataToHandle] = useState([]);
  const [checked, setChecked] = useState(null);
  const [selectedExploitationsTypes, setSelectedExploitationsTypes] = useState(null);
  const [selectedSectors, setSelectedSectors] = useState('');
  const [locationRoute, setLocationRoute] = useState('cooperative');
  const location = useLocation();
  const { user } = useAUTH();
  const navigate = useNavigate();
  const from = location?.state?.from;
  const nextCampaignChosen = location?.state?.nextCampaignChosen;
  const procedureExploitation = location?.state?.procedureExploitation;
  const checkStatus = useCheckStatus();
  const { t } = useTranslation();

  useEffect(() => {
    setLocationRoute(window.location.pathname.split('/')[1]);
  }, []);

  useEffect(() => {
    setChecked(
      exploitationsToImport?.map((exploitationToImport) => ({
        code: exploitationToImport.exploitation.code,
        checked: false,
      })),
    );
    setSelectedExploitationsTypes(
      exploitationsToImport?.map((exploitationToImport) => {
        if (exploitationToImport.exploitation.exploitationTypeName) {
          const foundExploitationType = exploitationToImport.type.filter(
            (d) =>
              d.exploitationTypeName === exploitationToImport.exploitation.exploitationTypeName,
          );
          if (foundExploitationType.length > 0) {
            return {
              code: exploitationToImport.exploitation?.code,
              exploitationType: foundExploitationType[0].id,
            };
          } else {
            return {
              code: exploitationToImport.exploitation?.code,
              exploitationType: exploitationToImport.type.find((d) => d.isDefaultType === true)?.id,
            };
          }
        } else {
          return {
            code: exploitationToImport.exploitation?.code,
            exploitationType: exploitationToImport.type.find((d) => d.isDefaultType === true)?.id,
          };
        }
      }),
    );
    setSelectedSectors(
      exploitationsToImport?.map((exploitationToImport) => ({
        code: exploitationToImport.exploitation?.code,
        sectorId: exploitationToImport.exploitation.sector?.id,
      })),
    );
  }, [exploitationsToImport, exploitationTypes]);

  useEffect(() => {
    setLoading(true);
    setPreflightJobId(location.state.jobId);
  }, [location]);

  useEffect(() => {
    const cooperativeId = localStorage.getItem('cooperative') ?? user.cooperative.id;
    if (!cooperativeId) {
      toast.info(t('message.selectCooperative'));
      navigate('/cooperative');
    }
    setCooperativeId(cooperativeId);
    loadExploitationTypes();
    loadCooperativeSectors(cooperativeId);
  }, [user]);

  const processExploitationType = (exploitationToImport) => {
    if (exploitationToImport.exploitation.exploitationTypeName) {
      const foundExploitationType = exploitationTypes.find(
        (d) => d.exploitationTypeName === exploitationToImport.exploitation.exploitationTypeName,
      );
      if (foundExploitationType) {
        return foundExploitationType.id;
      } else {
        return exploitationTypes.find((exploitationType) => exploitationType.isDefaultType === true)
          ?.id;
      }
    } else {
      return exploitationTypes.find((exploitationType) => exploitationType.isDefaultType === true)
        ?.id;
    }
  };

  const loadExploitationTypes = async () => {
    try {
      const result = await getExploitationTypes();
      setExploitationTypes(result.data.data);
    } catch (error) {
      toast.error(displayErrorMessage('ERR_GET_EXPLOITATION_TYPES'));
    }
  };

  const loadCooperativeSectors = async (cooperativeId) => {
    try {
      const result = await getSecteurLight(cooperativeId);
      setCooperativeSectors(result.data.data);
    } catch (error) {
      toast.error(displayErrorMessage('ERR_GET_COOPERATIVE_SECTORS'));
    }
  };

  useEffect(() => {
    if (cooperativeId && cooperativeSectors?.length && exploitationTypes?.length) {
      loadGeofoliaPreflight(preflightJobId);
    }
  }, [preflightJobId, cooperativeSectors, exploitationTypes]);

  const loadGeofoliaPreflight = async (jobId) => {
    try {
      const result = await getGeofoliaPreflight(jobId);
      const { data } = result.data;
      const processedData = data.map((exploitation) => ({
        exploitation,
        secteur: cooperativeSectors,
        type: exploitationTypes,
      }));
      setExploitationsToImport(processedData);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      toast.error(displayErrorMessage('ERR_IMPORT'));
      if (from === IMPORT_FROM.procedure) {
        navigate('/exploitation/campaign');
      } else {
        navigate('/cooperative/import');
      }
    }
  };

  const handleData = (data, indexExploitation) => {
    const processedData = {
      ...data,
      code: exploitationsToImport[indexExploitation].exploitation.code,
      name: exploitationsToImport[indexExploitation].exploitation.name,
    };
    setDataToHandle([processedData]);
  };

  const deleteData = (exploitationCode) => {
    setDataToHandle((prevDataToHandle) =>
      prevDataToHandle?.filter((prevDatumToHandle) => prevDatumToHandle.code !== exploitationCode),
    );
  };

  const cancelImport = () => {
    navigate(`/exploitation/campaign`, { state: { nextCampaignChosen } });
  };

  const sendData = async () => {
    if (!dataToHandle.length) {
      return toast.error(displayErrorMessage('ERR_NO_DATA_SELECTED'));
    } else {
      const missingData = dataToHandle.some(
        (datum) => !datum?.sectorId || !datum?.exploitationTypeId,
      );
      if (missingData) {
        return toast.error(displayErrorMessage('ERR_MISSING_DATA'));
      }
    }
    let formatedData = dataToHandle.map((exploitation) => ({
      sector: {
        id: exploitation.sectorId,
      },
      exploitationType: exploitation.exploitationTypeId,
      code: exploitation.code,
      name: exploitation.name,
    }));
    if (from === IMPORT_FROM.procedure) {
      formatedData = formatedData.map((exploitation) => ({
        ...exploitation,
        expectedForecastCampaign: nextCampaignChosen.stringFormat,
        procedureExploitation: Number(procedureExploitation),
      }));
    }
    try {
      toast.success(t('success.importStarted'));
      const response = await importParcelsGeofolia(preflightJobId, formatedData);
      const jobId = response?.data?.data?.jobId;
      checkStatus(jobId);
      setImportJobId(jobId);
    } catch (error) {
      toast.error(displayErrorMessage('ERR_IMPORT'));
    }
  };

  return (
    <div className="section">
      <div className="main_container">
        <h1 className="title_section">{t('title.geofoliaExploitationsToImport')}</h1>
        {loading ? (
          <Loader />
        ) : (
          <Table
            header={[
              '',
              t('navigation.exploitations'),
              t('label.assoliaSectors'),
              t('label.exploitationTypes'),
            ]}
            data={exploitationsToImport}
            RowElement={ExploitationTableRowGeofolia}
            handleData={handleData}
            deleteData={deleteData}
            checked={checked}
            setChecked={setChecked}
            selectedExploitationsTypes={selectedExploitationsTypes}
            setSelectedExploitationsTypes={setSelectedExploitationsTypes}
            selectedSectors={selectedSectors}
            setSelectedSectors={setSelectedSectors}
          />
        )}
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <div className="add_button_container">
          <Button text={t('common.validate')} onClick={() => sendData()} />
        </div>
        {from === IMPORT_FROM.procedure && (
          <div className="add_button_container">
            <Button text={t('common.cancel')} onClick={() => cancelImport()} />
          </div>
        )}
      </div>
    </div>
  );
}

export default GeofoliaProcess;
